import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Show, SignIn, SignUp } from "@clerk/react";
import { Toaster } from "@/components/ui/sonner";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import Landing from "@/pages/Landing";
import AdminLayout from "@/components/AdminLayout";
import DriverLayout from "@/components/DriverLayout";
import LoadsDashboard from "@/pages/LoadsDashboard";
import Trash from "@/pages/Trash";
import CreateLoad from "@/pages/CreateLoad";
import LoadDetail from "@/pages/LoadDetail";
import InvoicesList from "@/pages/InvoicesList";
import InvoiceView from "@/pages/InvoiceView";
import DriverLoads from "@/pages/DriverLoads";
import DriverLoadDetail from "@/pages/DriverLoadDetail";
import RoleGate from "@/components/RoleGate";
import Drivers from "@/pages/Drivers";
import Settings from "@/pages/Settings";
import Brokers from "@/pages/Brokers";
import OwnerConsole from "@/pages/OwnerConsole";
import { useMe } from "@/lib/useMe";
import { clearViewTenant } from "@/lib/viewTenant";

function AuthCard({ children }) {
  clearViewTenant();
  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2">
      <div className="hidden lg:flex relative items-end p-12 bg-slate-900 text-white overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1761133381018-aed5063d22fe?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-950/90 via-slate-900/60 to-transparent" />
        <div className="relative z-10 max-w-md">
          <div className="text-sm uppercase tracking-widest text-blue-300 mb-3">Fleet Sheet</div>
          <h2 className="text-4xl font-bold leading-tight">
            Every load in one tidy folder — ratecon, POD, invoice.
          </h2>
          <p className="mt-4 text-slate-200">
            Built for small carriers who want to get paid faster, with none of the paperwork chaos.
          </p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 lg:p-12 bg-white">
        {children}
      </div>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ClerkAuthProvider>
          <Routes>
            <Route path="/owner" element={<RoleGate expected="owner"><OwnerConsole /></RoleGate>} />
            <Route path="/" element={<HomeRouter />} />
            <Route
              path="/sign-in/*"
              element={
                <AuthCard>
                  <SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp />
                </AuthCard>
              }
            />
            <Route
              path="/sign-up/*"
              element={
                <AuthCard>
                  <SignUp routing="path" path="/sign-up" signInUrl="/sign-in" />
                </AuthCard>
              }
            />
            <Route
              path="/admin/*"
              element={
                <>
                  <Show when="signed-in">
                    <RoleGate expected="admin">
                      <AdminLayout>
                        <Routes>
                          <Route index element={<LoadsDashboard />} />
                          <Route path="loads/new" element={<CreateLoad />} />
                          <Route path="loads/:id" element={<LoadDetail />} />
                          <Route path="invoices" element={<InvoicesList />} />
                          <Route path="invoices/:id" element={<InvoiceView />} />
                          <Route path="drivers" element={<Drivers />} />
                          <Route path="trash" element={<Trash />} />
                          <Route path="import" element={<Navigate to="/admin" replace />} />
                          <Route path="settings" element={<Settings />} />
                          <Route path="brokers" element={<Brokers />} />
                        </Routes>
                      </AdminLayout>
                    </RoleGate>
                  </Show>
                  <Show when="signed-out">
                    <Navigate to="/sign-in" replace />
                  </Show>
                </>
              }
            />
            <Route
              path="/driver/*"
              element={
                <>
                  <Show when="signed-in">
                    <RoleGate expected="driver">
                      <DriverLayout>
                        <Routes>
                          <Route index element={<DriverLoads />} />
                          <Route path="loads/:id" element={<DriverLoadDetail />} />
                        </Routes>
                      </DriverLayout>
                    </RoleGate>
                  </Show>
                  <Show when="signed-out">
                    <Navigate to="/sign-in" replace />
                  </Show>
                </>
              }
            />
          </Routes>
          <Toaster richColors position="top-right" />
        </ClerkAuthProvider>
      </BrowserRouter>
    </div>
  );
}

function HomeRouter() {
  return (
    <>
      <Show when="signed-out">
        <Landing />
      </Show>
      <Show when="signed-in">
        <RoleRedirect />
      </Show>
    </>
  );
}

function RoleRedirect() {
  const { me, loading } = useMe();
  if (loading) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  if (!me) return <Navigate to="/sign-in" replace />;
  return <Navigate to={me.is_owner ? "/owner" : me.role === "admin" ? "/admin" : "/driver"} replace />;
}

export default App;
